import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../services/api';

const ICON = {
  review_approved: { emoji: '✓', bg: 'bg-green-100 text-green-700' },
  review_returned: { emoji: '↩', bg: 'bg-yellow-100 text-yellow-700' },
  review_rejected: { emoji: '✗', bg: 'bg-red-100 text-red-700' },
};

const POLL_MS = 30_000; // poll every 30 s

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count (light, runs on a timer)
  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.unreadCount();
      setUnread(res.data.count ?? 0);
    } catch { /* ignore */ }
  }, []);

  // Fetch full list (runs when dropdown opens)
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list();
      setNotifications(res.data || []);
      setUnread((res.data || []).filter(n => !n.is_read).length);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  // Poll count
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Fetch list when opened
  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (id) => {
    await notificationsAPI.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const handleNotificationClick = async (n) => {
    if (!n.is_read) await handleMarkRead(n.id);
    if (n.type === 'review_returned') {
      setOpen(false);
      if (n.target_id) {
        navigate(`/history?returned=${n.target_id}`);
      } else {
        // Prodigy annotation returned: go to History where ReannotationPanel lives
        navigate('/history');
      }
    }
  };

  const handleMarkAll = async () => {
    await notificationsAPI.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Notificaciones"
        className="relative p-2 rounded-full text-gray-600 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {/* Bell SVG */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {/* Badge */}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <span className="text-sm font-semibold text-gray-800">Notificaciones</span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-xs text-blue-600 hover:underline"
              >Marcar todas como leídas</button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">Sin notificaciones</div>
            ) : (
              notifications.map(n => {
                const icon = ICON[n.type] || { emoji: '•', bg: 'bg-gray-100 text-gray-600' };
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex gap-3 px-4 py-3 transition-colors ${
                      n.type === 'review_returned' ? 'cursor-pointer hover:bg-yellow-50' : 'cursor-default hover:bg-gray-50'
                    } ${!n.is_read ? 'bg-blue-50' : ''}`}
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${icon.bg}`}>
                      {icon.emoji}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {n.message}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{fmt(n.created_at)}</p>
                    </div>
                    {/* Mark read */}
                    {!n.is_read && (
                      <button
                        onClick={() => handleMarkRead(n.id)}
                        title="Marcar como leída"
                        className="flex-shrink-0 self-start mt-1 w-2 h-2 bg-blue-500 rounded-full hover:bg-blue-700"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
